import { useCallback, useEffect, useState } from 'react';

import { Activity } from '@website/services/activities.service';

interface UseActivitySelectionParams {
  activities?: Activity[];
  selectedActivityId?: string | undefined;
  setSelectedActivityId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectionSource?: 'map' | 'list' | null;
  setSelectionSource: React.Dispatch<React.SetStateAction<'map' | 'list' | null>>;
}

export function useActivitySelection({
  activities,
  selectedActivityId,
  setSelectedActivityId,
  selectionSource: _selectionSource, // Property name must match interface
  setSelectionSource
}: UseActivitySelectionParams) {
  // We don't use _selectionSource in this hook, but we keep it as part of the interface
  // for consistency with the calling component
  // Hover state (still internal since it's not lifted up)
  const [hoveredActivityId, setHoveredActivityId] = useState<string | null>(null);

  // Drawer state
  const [activityForDrawerData, setActivityForDrawerData] = useState<Activity | null>(null);

  // Handle location/activity selection - stable function reference
  const handleLocationSelect = useCallback(
    (activityId: string, source: 'map' | 'list' = 'map') => {
      console.log('Activity selection triggered for ID:', activityId, 'from', source);

      // If selecting the already selected activity, deselect it
      if (selectedActivityId === activityId) {
        console.log('Deselecting current activity');
        setSelectedActivityId(undefined);
        setSelectionSource(null);
      } else {
        console.log('Selecting new activity');
        setSelectedActivityId(activityId);
        setSelectionSource(source);
      }
    },
    [selectedActivityId, setSelectedActivityId, setSelectionSource]
  );

  // Handle drawer close - stable function reference
  const handleDrawerClose = useCallback(() => {
    setSelectedActivityId(undefined);
    setSelectionSource(null);
    setActivityForDrawerData(null);
  }, [setSelectedActivityId, setSelectionSource]);

  // Handle activity hover - stable function reference
  const handleActivityHover = useCallback((activityId: string) => {
    setHoveredActivityId(activityId);
  }, []);

  // Handle activity leave hover - stable function reference
  const handleActivityLeaveHover = useCallback(() => {
    setHoveredActivityId(null);
  }, []);

  // Effect to find and set the selected activity when ID changes
  useEffect(() => {
    console.log('Selected activity ID changed:', selectedActivityId);
    if (selectedActivityId) {
      const foundActivity = activities?.find((act: Activity) => act.id === selectedActivityId);
      console.log('Found activity:', foundActivity ? 'yes' : 'no');
      setActivityForDrawerData(foundActivity || null);
    } else {
      setActivityForDrawerData(null);
    }
  }, [selectedActivityId, activities]);

  return {
    // State (hover and drawer are still internal, selection state comes from props)
    hoveredActivityId,
    activityForDrawerData,

    // Handlers (now with stable references)
    handleLocationSelect,
    handleDrawerClose,
    handleActivityHover,
    handleActivityLeaveHover
  };
}
